import React from "react";
import { Avatar, Grid, Typography } from "@sumprio/ui";
import type { BodySize } from "@sumprio/ui";

export interface AvatarNameRowProps {
  initials: string;
  value: string;
  label?: string;
  secondaryText?: string;
  nameSuffix?: React.ReactNode;
  avatarSize?: "sm" | "md" | "lg";
  avatarSrc?: string | null;
  trailing?: React.ReactNode;
  gap?: "sm" | "md" | "lg";
  valueBodySize?: BodySize;
  onClick?: () => void;
  ariaLabel?: string;
}

export default function AvatarNameRow({
  initials,
  value,
  label,
  secondaryText,
  nameSuffix,
  avatarSize = "sm",
  avatarSrc,
  trailing,
  gap = "md",
  valueBodySize = "sm",
  onClick,
  ariaLabel,
}: AvatarNameRowProps) {
  const hasPhoto = Boolean(avatarSrc?.trim());
  const interactive = Boolean(onClick);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <Grid
      display="flex"
      alignItems="center"
      gap={gap}
      paddingX="lg"
      {...(interactive
        ? {
            role: "button" as const,
            tabIndex: 0,
            onClick,
            onKeyDown: handleKeyDown,
            "aria-label": ariaLabel,
            style: { cursor: "pointer" as const },
          }
        : {})}
    >
      <Avatar
        type={hasPhoto ? "image" : "initial"}
        size={avatarSize}
        initials={initials}
        src={hasPhoto ? avatarSrc! : undefined}
      />
      <Grid display="flex" flexDirection="column" flex={1} minWidth={0} gap={0}>
        {label !== null && label !== "" && (
          <Typography variant="body" bodySize="xs" color="tertiary">
            {label}
          </Typography>
        )}
        <Typography variant="body" bodySize={valueBodySize} bodyWeight="medium" color="primary">
          {value}
        </Typography>
        {nameSuffix !== null && nameSuffix !== undefined ? (
          <Grid
            marginTop="xs"
            style={{ whiteSpace: "nowrap", alignSelf: "flex-start", minWidth: "min-content" }}
          >
            {nameSuffix}
          </Grid>
        ) : null}
        {secondaryText !== null && secondaryText !== "" && (
          <Typography variant="body" bodySize="xs" color="tertiary">
            {secondaryText}
          </Typography>
        )}
      </Grid>
      {trailing !== null ? trailing : null}
    </Grid>
  );
}
